import { compare } from 'owa-datetime';
import { EventEntity } from '../schema/EventEntity';
import { updateEventProperties } from './updateEventProperties';

interface EventsToAddBatch<T extends EventEntity> {
    startIndex: number;
    eventsToAdd: T[];
    finished: boolean;
}

export function upsertEvents<T extends EventEntity>(
    eventsToAdd: T[],
    events: T[],
    eventsStartIndex: number = 0,
    forceOverrideAllPropertiesForExistingEvents: boolean = false
): T[] {
    // Making a copy as we don't want to mutate the list that is passed
    const updateEventsResult = removeExistingEventsAndGetNewEventsToAdd(
        eventsToAdd,
        events,
        forceOverrideAllPropertiesForExistingEvents
    );
    eventsToAdd = updateEventsResult.newEventsToAdd;

    const eventsToAddBatches: EventsToAddBatch<T>[] = [
        { startIndex: -1, eventsToAdd: [], finished: false },
    ];

    // As the current batch is always the last one so when we want to use it, call
    // this function to get it.
    const getCurrentBatch = () => eventsToAddBatches[eventsToAddBatches.length - 1];

    let eventsIndex = eventsStartIndex;
    let eventsToAddIndex = 0;

    while (eventsIndex < events.length && eventsToAddIndex < eventsToAdd.length) {
        const currentEvent = events[eventsIndex];
        const currentEventToAdd = eventsToAdd[eventsToAddIndex];

        const dateComparisonValue = compare(currentEventToAdd.Start, currentEvent.Start);
        const isEventToAddBeforeCurrentEvent = dateComparisonValue === -1;
        const isEventToAddSameTimeAsCurrentEvent = dateComparisonValue === 0;
        const isEventToAddAfterCurrentEvent = dateComparisonValue === 1;

        if (isEventToAddSameTimeAsCurrentEvent) {
            // If the event to add is during the same time as the current
            // event then we just add it to the list before it.

            addEventToBatch(currentEventToAdd, eventsIndex, eventsToAddBatches);
            eventsToAddIndex++;
        } else if (isEventToAddBeforeCurrentEvent) {
            // If the event to add is before current event in the list
            // then we should add it to the batch so that it is inserted
            // before this current event.
            addEventToBatch(currentEventToAdd, eventsIndex, eventsToAddBatches);
            eventsToAddIndex++;
        } else if (isEventToAddAfterCurrentEvent) {
            // If the event to add is after current event then
            // we should finsih the old batch and keep moving forward
            // in the events list to see where the newer event
            // should be added.
            getCurrentBatch().finished = true;
            eventsIndex++;
        }
    }

    if (eventsToAddIndex < eventsToAdd.length) {
        // If there are some events left to add then add to the batch
        const eventsLeftToAdd = eventsToAdd.slice(eventsToAddIndex, eventsToAdd.length);
        eventsToAddBatches.push({
            startIndex: events.length,
            eventsToAdd: eventsLeftToAdd,
            finished: true,
        });
    }

    eventsToAddBatches
        .filter(e => e.eventsToAdd.length !== 0)
        .reverse() // We reverse it so that we add the list of events from the end so that the startIndex computed before stay correct
        .forEach(e => events.splice(e.startIndex, 0, ...e.eventsToAdd));

    const eventsAdded = [...eventsToAdd];
    sortEventsByStartTimeAsc(eventsAdded);

    return eventsAdded;
}

function addEventToBatch<T extends EventEntity>(
    eventToAdd: T,
    currentIndex: number,
    batches: EventsToAddBatch<T>[]
) {
    const currentBatch = batches[batches.length - 1];
    if (currentBatch.startIndex === -1) {
        // If there was nothing added to batch before then
        // it should start adding events from the current
        // index.
        currentBatch.startIndex = currentIndex;
        currentBatch.eventsToAdd.push(eventToAdd);
    } else if (currentBatch.finished) {
        // If the current batch was already finished then
        // create a new one.
        const newBatch = {
            startIndex: currentIndex,
            eventsToAdd: [eventToAdd],
            finished: false,
        };
        batches.push(newBatch);
    } else {
        // Otherwise add to existing batch
        currentBatch.eventsToAdd.push(eventToAdd);
    }
}

function removeExistingEventsAndGetNewEventsToAdd<T extends EventEntity>(
    eventsToAdd: T[],
    events: T[],
    forceOverrideAllPropertiesForExistingEvents: boolean
): { newEventsToAdd: T[] } {
    const eventsToIndex = getEventsToIndexMap(events);

    const newEventsToAdd: T[] = [];
    const eventsToRemoveIndex: number[] = [];

    for (let i = 0; i < eventsToAdd.length; i++) {
        const eventToAdd = eventsToAdd[i];
        const existingEventIndex: number | undefined = eventsToIndex[eventToAdd.Key];

        // Check for undefined only as 0 is a valid index but is falsy
        if (existingEventIndex !== undefined) {
            // As we found an item that already exists we assign properties from
            // the event to add to the event instance we have in cache so that
            // no reference changes.
            const event = events[existingEventIndex];

            updateEventProperties(event, eventToAdd, forceOverrideAllPropertiesForExistingEvents);

            // We remove any existing item from the list so that
            // in case if time has changed for it then it will be re-added
            // in the correct position
            eventsToRemoveIndex.push(existingEventIndex);
            newEventsToAdd.push(event);
        } else {
            newEventsToAdd.push(eventToAdd);
        }
    }

    // We sort the list of indexes in descending order
    // so we can start removing events from the end
    getUniqueNumbers(eventsToRemoveIndex)
        .sort((a, b) => b - a)
        .forEach(index => events.splice(index, 1));

    return { newEventsToAdd };
}

function getEventsToIndexMap<T extends EventEntity>(events: T[]): { [key: string]: number } {
    return events.reduce<{ [key: string]: number }>((agg, event, index) => {
        agg[event.Key] = index;
        return agg;
    }, {});
}

function sortEventsByStartTimeAsc<T extends EventEntity>(events: T[]) {
    events.sort((e1, e2) => compare(e1.Start, e2.Start));
}

function getUniqueNumbers(list: number[]): number[] {
    const mapOfNum = list.reduce<{ [index: number]: number }>((agg, num) => {
        agg[num] = num;
        return agg;
    }, {});

    return Object.keys(mapOfNum).map(k => mapOfNum[k]);
}
